import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Badge, Modal, Form, FormGroup, FormLabel, FormControl } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { CiWarning } from "react-icons/ci";
import { FaCheck, FaDownload, FaShare, FaUndo, FaTimes } from 'react-icons/fa';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import { transactionApi, refundApi, Transaction, CreateRefundParams } from './TransactionApi.tsx';
import IconGiftType from "../../../images/orders/icon_gift-type.png";
import IconTransaction from "../../../images/orders/icon_trasaction.png";
import IconCard from "../../../images/orders/icon_card.png";
import IconCalendarOutlook from "../../../images/orders/icon_calendar-outlook.png";
import IconCurrency from "../../../images/orders/icon_curr_icon.png";
import IconCurrencyOutline from "../../../images/orders/icon_curr-outline_icon.png";


import "./TransactionDetails.scss";

interface TransactionDetailsProps {}

const TransactionDetails: React.FC<TransactionDetailsProps> = () => {
  const { transactionId, type } = useParams<{ transactionId: string; type: string }>();
  const navigate = useNavigate();
  const { withLoader } = useApiLoader();

  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showShareReceiptModal, setShowShareReceiptModal] = useState(false);
  const [refundForm, setRefundForm] = useState({
    refundType: 'Full Refund' as 'Full Refund' | 'Partial Refund',
    amount: 0,
    reason: '',
    additionalNotes: ''
  });
  const [refundLoading, setRefundLoading] = useState(false);
  const [shareReceiptLoading, setShareReceiptLoading] = useState(false);

  // Formik validation schema for refund
  const refundValidationSchema = Yup.object({
    reason: Yup.string()
      .min(10, 'Reason must be at least 10 characters long')
      .max(500, 'Reason must not exceed 500 characters')
      .required('Refund reason is required'),
    amount: Yup.number()
      .positive('Amount must be positive')
      .max(transaction?.amountPaid || 0, 'Amount cannot exceed transaction amount')
      .when('refundType', {
        is: 'Partial Refund',
        then: (schema) => schema.required('Amount is required for partial refund')
      }),
    additionalNotes: Yup.string()
      .max(1000, 'Additional notes must not exceed 1000 characters')
  });

  // Formik validation schema for share receipt
  const shareReceiptValidationSchema = Yup.object({
    emails: Yup.array()
      .of(
        Yup.string()
          .email('Invalid email address')
          .required('Email is required')
      )
      .min(1, 'At least one email is required')
      .test('has-valid-email', 'At least one valid email is required', function(emails) {
        return emails && emails.length > 0 && emails.some(email => email && email.trim() !== '');
      })
  });

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const fetchTransactionDetails = async () => {
    // Get the full transaction ID from sessionStorage (stored when clicked from list)
    const fullTransactionId = sessionStorage.getItem('currentTransactionId');

   

    if (!fullTransactionId || !type) {
      console.error('No transaction ID or type provided');
      toast.error('Transaction ID and type are required');
      navigate('/transactions');
      return;
    }

    try {
      setLoading(true);
      const response = await withLoader(
        () => transactionApi.getTransactionById(fullTransactionId, type),
        'Loading transaction details...'
      );


      if (response.status === 'Success' && response.data) {
        setTransaction(response.data);
        setRefundForm(prev => ({
          ...prev,
          amount: response.data?.amountPaid || 0
        }));
      } else {
        console.error('API Error:', response);
        toast.error(response.message || 'Failed to fetch transaction details');
        navigate('/transactions');
      }
    } catch (error: any) {
      console.error('Error fetching transaction details:', error);
      toast.error('Failed to fetch transaction details');
      navigate('/transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!transactionId) return;

    try {
      const blob = await withLoader(
        () => transactionApi.downloadReceipt(transactionId),
        'Downloading receipt...'
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Receipt downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  const handleRefundSubmit = async (values: any) => {
    if (!transaction || !transactionId) return;

    try {
      setRefundLoading(true);
      setShowRefundModal(false);
      setShowProcessingModal(true);

      const refundAmount = values.refundType === 'Full Refund' ? (transaction?.amountPaid || 0) : values.amount;

      const refundParams: CreateRefundParams = {
        transactionId,
        refundAmount: Number(refundAmount),
        reason: values.reason.trim(),
        transactionType: "gift",
        additionalNotes: values.additionalNotes?.trim() || undefined
      };

      // Step 1: Create refund request
      const [createResponse] = await Promise.all([
        refundApi.createRefund(refundParams),
        new Promise(resolve => setTimeout(resolve, 2500)) // 2.5-second delay for create
      ]);

      if (createResponse.status !== 'Success' || !createResponse.data) {
        setShowProcessingModal(false);
        if (createResponse.errors && createResponse.errors.length > 0) {
          createResponse.errors.forEach((error: string) => {
            toast.error(error);
          });
        } else {
          toast.error(createResponse.message || 'Failed to create refund request');
        }
        return;
      }

      // Step 2: Process the refund using the returned refund ID
      const refundId = createResponse.data.refundId || createResponse.data._id;

      const [processResponse] = await Promise.all([
        refundApi.processRefund(refundId, {
          status: 'processed',
          adminNotes: 'Refund processed by admin'
        }),
        new Promise(resolve => setTimeout(resolve, 2500)) // 2.5-second delay for process
      ]);

      setShowProcessingModal(false);

      if (processResponse.status === 'Success') {
        
        setShowSuccessModal(true);
        fetchTransactionDetails();
      } else {
        toast.error(processResponse.message || 'Failed to process refund');
      }
    } catch (error: any) {
      console.error('Error processing refund:', error);
      setShowProcessingModal(false);
      toast.error('Failed to process refund request');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleShareReceiptSubmit = async (values: any) => {
    if (!transaction || !transactionId) return;

    try {
      setShareReceiptLoading(true);

      // Filter out empty emails
      const validEmails = values.emails.filter((email: string) => email && email.trim() !== '');


      // Call API to share receipt
      const response = await withLoader(
        () => transactionApi.shareReceipt(transactionId, validEmails),
        'Sharing receipt...'
      );

      if (response.status === 'Success') {
        toast.success(`Receipt shared successfully to ${validEmails.length} email(s)`);
        setShowShareReceiptModal(false);
      } else {
        toast.error(response.message || 'Failed to share receipt');
      }
    } catch (error: any) {
      console.error('Error sharing receipt:', error);
      toast.error('Failed to share receipt');
    } finally {
      setShareReceiptLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    let badgeClass = '';
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'success':
      case 'successful':
        badgeClass = 'success';
        break;
      case 'failed':
        badgeClass = 'danger';
        break;
      case 'refunded':
        badgeClass = 'info';
        break;
      case 'pending':
        badgeClass = 'warning';
        break;
      default:
        badgeClass = 'secondary';
    }
    return <Badge bg={badgeClass}>{status}</Badge>;
  };

  useEffect(() => {
    fetchTransactionDetails();
  }, [transactionId, type]);

  if (loading) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading transaction details...</p>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  if (!loading && !transaction) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <p className="text-muted">Transaction not found</p>
              <Button variant="primary" onClick={() => navigate('/transactions')}>
                Back to Transactions
              </Button>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  if (!transaction) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading transaction details...</p>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  return (
    <section className='main-body'>
      <Container fluid className="p-0">
        <Card.Body>
          {/* Breadcrumb */}
          <div className="d-flex align-items-center mb-4">
            <Button
              variant="link"
              className="text-decoration-none p-0 fw-bold d-flex align-items-center gap-2 btn btn-link me-2 btn btn-link"
              style={{color: '#3957a0'}}
              onClick={() => navigate('/transactions')}
            >
              ← Back
            </Button>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <span className="text-muted">Support</span>
                </li>
                <li className="breadcrumb-item active">
                  {transaction?.transactionId || transactionId}
                </li>
              </ol>
            </nav>
          </div>

          {/* Combined Details Block */}
          <div className="details-block">
            <div className="details-block-in">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="section-title mb-0 fw-bold" style={{fontSize:'20px'}}>Transaction Details</h2>
                {/* <div className="d-flex gap-2">
                  {getStatusBadge(transaction?.status || 'N/A')}
                </div> */}
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconTransaction} alt="Transaction ID" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Transaction ID</label>
                        <p className="fw-bold mb-0">{transaction?.transactionId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconCalendarOutlook} alt="Date & Time" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Date & Time</label>
                        <p className="fw-bold mb-0">{transaction?.date ? formatDateTime(transaction.date) : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconCard} alt="Payment Method" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Payment Method</label>
                        <p className="fw-bold mb-0">{transaction?.paymentMethod || 'Credit Card ****8304'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconCurrency} alt="Amount" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Amount</label>
                        <p className="fw-bold mb-0">{transaction?.amountPaid ? formatCurrency(transaction.amountPaid) : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconGiftType} alt="Transaction Type" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Transaction Type</label>
                        <p className="fw-bold mb-0">{transaction?.type || 'Gift'}</p>
                      </div>
                    </div>
                  </div>
                </div>

              <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconCurrencyOutline} alt="Order ID" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Order ID</label>
                        <p className="fw-bold mb-0">{(transaction as any)?.giftDetails?.orderId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconTransaction} alt="Status" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Status</label>
                        <div className="fw-bold mb-0">
                          {getStatusBadge(transaction?.status || 'N/A')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Flow Timeline - Commented out as it contains static data */}
          {/* <div className="payment-flow-section mt-5 p-3 rounded">
            <div className="progress-timeline">
              <div className="progress-line"></div>
              <div className="row">
                <div className="col-md-3">
                  <div className="payment-step text-center position-relative">
                    <div className="step-icon mb-2 position-relative">
                      <div className="step-circle">
                        <FaCheck className="text-white" size={20} />
                      </div>
                    </div>
                    <h6 className="fw-bold">Order Initiated</h6>
                    <p className="small mb-1">Customer started the checkout process</p>
                    <small className="text-muted">10:25:00 AM</small>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="payment-step text-center position-relative">
                    <div className="step-icon mb-2 position-relative">
                      <div className="step-circle">
                        <FaCheck className="text-white" size={20} />
                      </div>
                    </div>
                    <h6 className="fw-bold">Payment Processing</h6>
                    <p className="small mb-1" style={{color:"#677289", fontSize:'16px'}}>Processing payment through secure gateway</p>
                    <small className="text-muted">10:25:16 AM</small>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="payment-step text-center position-relative">
                    <div className="step-icon mb-2 position-relative">
                      <div className="step-circle">
                        <FaCheck className="text-white" size={20} />
                      </div>
                    </div>
                    <h6 className="fw-bold">Payment Authorized</h6>
                    <p className="text-muted small mb-1">Credit card authorization successful</p>
                    <small className="text-muted">10:25:34 AM</small>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="payment-step text-center position-relative">
                    <div className="step-icon mb-2 position-relative">
                      <div className="step-circle">
                        <FaCheck className="text-white" size={20} />
                      </div>
                    </div>
                    <h6 className="fw-bold">Payment Completed</h6>
                    <p className="text-muted small mb-1">Transaction successfully processed</p>
                    <small className="text-muted">10:25:45 AM</small>
                  </div>
                </div>
              </div>
            </div>

          </div> */}

          {/* Action Buttons */}
          <div className="action-buttons mt-4">
            <Button
              variant="outline-primary"
              className="px-4 py-2"
              onClick={handleDownloadReceipt}
            >
              <FaDownload className="me-2" />
              Download Receipt
            </Button>
            <Button
              variant="outline-primary"
              className="px-4 py-2"
              onClick={() => setShowShareReceiptModal(true)}
            >
              <FaShare className="me-2" />
              Share Receipt
            </Button>
            <Button
              variant="outline-primary"
              className="px-4 py-2"
              onClick={() => setShowRefundModal(true)}
              disabled={transaction?.type?.toLowerCase() === 'subscription' || transaction?.type?.toLowerCase() === 'refund'}
              title={
                transaction?.type?.toLowerCase() === 'subscription'
                  ? 'Refund not available for subscription transactions'
                  : transaction?.type?.toLowerCase() === 'refund'
                  ? 'Refund not available for refund transactions'
                  : ''
              }
            >
              <FaUndo className="me-2" />
              Refund
            </Button>
          </div>
            </div>
          </div>

          

          {/* Refund Modal */}
          <Modal show={showRefundModal} onHide={() => setShowRefundModal(false)} size="lg">
            <Modal.Header closeButton className='pb-0 border-0'>
              <Modal.Title className='fw-bold'>Process Refund</Modal.Title>
            </Modal.Header>
            <Formik
              initialValues={{
                refundType: 'Full Refund',
                amount: transaction?.amountPaid || 0,
                reason: '',
                additionalNotes: ''
              }}
              validationSchema={refundValidationSchema}
              onSubmit={handleRefundSubmit}
              enableReinitialize
            >
              {({ values, setFieldValue, errors, touched, isValid }) => (
                <FormikForm>
                  <Modal.Body>
                    <p className='mb-0' style={{fontSize:'16px', color:'#677289'}}>
                      Transaction ID:
                        <strong>{transaction?.transactionId || 'N/A'}</strong>
                      </p>
                    <p className='mb-0' style={{fontSize:'16px', color:'#677289'}}>Amount:<strong>{transaction?.amountPaid ? formatCurrency(transaction.amountPaid) : 'N/A'}</strong></p>
                    <p style={{fontSize:'12px', color:'#677289', }}><CiWarning size={16}/> <strong >Important:</strong><span style={{fontStyle:'regular'}}>This action cannot be undone. The refund will be processed to the original payment method.</span></p>
                    <hr/>
                    <Row>
                      <Col md={12}>
                        <FormGroup className="mb-3">
                          <FormLabel>Refund Amount *</FormLabel>
                          <div className="mb-2 d-flex gap-3">
                            <Field name="refundType">
                              {({ field }) => (
                                <>
                                  <Form.Check
                                    type="radio"
                                    name="refundType"
                                    id="fullRefund"
                                    label="Full Refund"
                                    checked={values.refundType === 'Full Refund'}
                                    onChange={() => {
                                      setFieldValue('refundType', 'Full Refund');
                                      setFieldValue('amount', transaction?.amountPaid || 0);
                                    }}
                                  />
                                  <Form.Check
                                    type="radio"
                                    name="refundType"
                                    id="partialRefund"
                                    label="Partial Refund"
                                    checked={values.refundType === 'Partial Refund'}
                                    onChange={() => setFieldValue('refundType', 'Partial Refund')}
                                  />
                                </>
                              )}
                            </Field>
                          </div>
                          <Field name="amount">
                            {({ field }) => (
                              <Form.Control
                                {...field}
                                type="text"
                                placeholder={values.refundType === 'Full Refund' ? 'Full refund amount' : 'Enter amount'}
                                disabled={values.refundType === 'Full Refund'}
                                isInvalid={errors.amount && touched.amount}
                                className='p-3'
                                onKeyDown={(e) => {
                                  // Prevent non-numeric characters except for allowed keys
                                  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End', '.'];
                                  if (!allowedKeys.includes(e.key) && (e.key < '0' || e.key > '9')) {
                                    e.preventDefault();
                                  }
                                }}
                                onPaste={(e) => {
                                  // Prevent pasting non-numeric content
                                  const pastedText = e.clipboardData.getData('text');
                                  if (!/^\d*\.?\d*$/.test(pastedText)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="amount" component="div" className="text-danger small mt-1" />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup className="mb-3">
                          <FormLabel>Refund Reason *</FormLabel>
                          <Field name="reason">
                            {({ field }) => (
                              <Form.Control
                                {...field}
                                type="text"
                                placeholder="Enter refund reason (minimum 10 characters)"
                                isInvalid={errors.reason && touched.reason}
                                className='p-3'
                              />
                            )}
                          </Field>
                          <ErrorMessage name="reason" component="div" className="text-danger small mt-1" />
                          <div className="text-muted small mt-1">
                            {values.reason.length}/10 characters minimum
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup className="mb-3">
                      <FormLabel>Additional Notes</FormLabel>
                      <Field name="additionalNotes">
                        {({ field }) => (
                          <Form.Control
                            {...field}
                            as="textarea"
                            rows={3}
                            placeholder="Enter additional details (optional)"
                            isInvalid={errors.additionalNotes && touched.additionalNotes}
                            className='p-3'
                          />
                        )}
                      </Field>
                      <ErrorMessage name="additionalNotes" component="div" className="text-danger small mt-1" />
                      <div className="text-muted small mt-1">
                        {values.additionalNotes.length}/1000 characters maximum
                      </div>
                    </FormGroup>

                    <Card className="bg-light">
                      <Card.Body>
                        <h6>Refund Summary</h6>
                        <div className="d-flex justify-content-between">
                          <span>Original Amount:</span>
                          <span>{transaction?.amountPaid ? formatCurrency(transaction.amountPaid) : 'N/A'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Processing Fee:</span>
                          <span>$2.99</span>
                        </div>
                        <hr />
                        <div className="d-flex justify-content-between">
                          <strong>Refund Amount:</strong>
                          <strong>{formatCurrency((values.refundType === 'Full Refund' ? (transaction?.amountPaid || 0) : values.amount) - 2.99)}</strong>
                        </div>
                      </Card.Body>
                    </Card>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={refundLoading || !isValid}
                    >
                      {refundLoading ? 'Processing...' : 'Process Refund'}
                    </Button>
                  </Modal.Footer>
                </FormikForm>
              )}
            </Formik>
          </Modal>

          {/* Processing Modal */}
          <Modal
            show={showProcessingModal}
            centered
            backdrop="static"
            keyboard={false}
            size="sm"
          >
            <Modal.Body className="text-center py-4">
              <div className="processing-modal mb-3">
                <div className="spinner-border-custom" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
              <h5 className="mb-2">Processing Refund...</h5>
              <p className="text-muted mb-0">
                Please wait while we process the refund.<br />
                This may take a few moments.
              </p>
            </Modal.Body>
          </Modal>

          {/* Success Modal */}
          <Modal
            show={showSuccessModal}
            centered
            size="sm"
            onHide={() => setShowSuccessModal(false)}
          >
            <Modal.Body className="text-center py-4">
              <div className="success-modal mb-3">
                <div className="success-icon">
                  <FaCheck className="text-white" size={40} />
                </div>
              </div>
              <h5 className="mb-2">Refund Processed Successfully!</h5>
              <p className="text-muted mb-3">
                The refund has been processed and will appear on the<br />
                customer's original payment method within 3-5 business days.
              </p>
              <Button
                variant="outline-primary"
                onClick={() => setShowSuccessModal(false)}
                className="px-4"
              >
                Close
              </Button>
            </Modal.Body>
          </Modal>

          {/* Share Receipt Modal */}
          <Modal
            className="main-modal"
            show={showShareReceiptModal}
            onHide={() => setShowShareReceiptModal(false)}
            centered
          >
            <Modal.Header closeButton className="px-0 pb-0 border-0">
              <Modal.Title className="fw-bold">Share receipt</Modal.Title>
            </Modal.Header>
            <Formik
              initialValues={{
                emails: ['']
              }}
              validationSchema={shareReceiptValidationSchema}
              onSubmit={handleShareReceiptSubmit}
              enableReinitialize
            >
              {({ values, setFieldValue, errors, touched, isValid }) => {
                // Check if first email is valid and not empty
                const firstEmailValid = values.emails[0] &&
                                       values.emails[0].trim() !== '' &&
                                       /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.emails[0]);

                return (
                  <FormikForm>
                    <Modal.Body className="px-0">
                      <p className="mb-3" style={{ fontSize: '16px', color: '#677289' }}>
                        Transaction ID: <strong>{transaction?.transactionId || 'N/A'}</strong>
                      </p>

                      <FormGroup className="form-group">
                        <FormLabel>Email</FormLabel>
                        {values.emails.map((email, index) => (
                          <div key={index} className="mb-3">
                            <div className="d-flex align-items-start gap-2">
                              <div className="flex-grow-1">
                                <Field name={`emails.${index}`}>
                                  {({ field }) => (
                                    <FormControl
                                      {...field}
                                      type="email"
                                      placeholder="Enter Email"
                                      isInvalid={
                                        touched.emails?.[index] &&
                                        errors.emails?.[index] !== undefined
                                      }
                                      className="p-3"
                                    />
                                  )}
                                </Field>
                                <ErrorMessage
                                  name={`emails.${index}`}
                                  component="div"
                                  className="text-danger small mt-1"
                                />
                              </div>
                              {/* Show remove button for all emails except the first one */}
                              {index > 0 && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="d-flex align-items-center justify-content-center"
                                  style={{
                                    minWidth: '40px',
                                    height: '50px',
                                    marginTop: '0px'
                                  }}
                                  onClick={() => {
                                    const newEmails = values.emails.filter((_, i) => i !== index);
                                    setFieldValue('emails', newEmails);
                                  }}
                                  title="Remove this email"
                                >
                                  <FaTimes />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Show Add Another Email only if first email is valid */}
                        {firstEmailValid && (
                          <Button
                            variant="link"
                            className="p-0 text-primary"
                            style={{ fontSize: '14px', textDecoration: 'none' }}
                            onClick={() => {
                              setFieldValue('emails', [...values.emails, '']);
                            }}
                          >
                            + Add Another Email
                          </Button>
                        )}
                      </FormGroup>
                    </Modal.Body>
                    <Modal.Footer className="px-0">
                      <Button
                        className="main-bttn w-100"
                        type="submit"
                        disabled={shareReceiptLoading || !isValid}
                        style={{
                          backgroundColor: '#3957A0',
                          border: 'none',
                          padding: '12px'
                        }}
                      >
                        {shareReceiptLoading ? 'Sharing...' : 'Share Receipt'}
                      </Button>
                    </Modal.Footer>
                  </FormikForm>
                );
              }}
            </Formik>
          </Modal>
        </Card.Body>
      </Container>
    </section>
  );
};

export default TransactionDetails;